<template>
  <div class="fixed inset-0 bg-slate-900/60 z-50 flex items-center justify-center p-4" @click.self="$emit('close')">
    <div class="bg-white rounded-q4cl shadow-card-hover w-full max-w-2xl border border-q4cl-border flex flex-col">
      <header class="px-5 py-3 border-b border-q4cl-border flex items-center justify-between flex-shrink-0">
        <h3 class="font-bold text-q4cl-primary">{{ $t('署名してください') }}</h3>
        <button class="btn-icon" type="button" @click="$emit('close')" :aria-label="$t('閉じる')"><i class="fa-solid fa-xmark" aria-hidden="true"></i></button>
      </header>
      <div class="p-4">
        <div ref="wrapRef" class="relative border border-q4cl-border rounded-q4cl bg-white overflow-hidden" style="aspect-ratio: 5 / 2; min-height: 200px;">
          <canvas ref="canvasRef" class="absolute inset-0 w-full h-full touch-none cursor-crosshair" style="display: block;" :aria-label="$t('署名キャンバス')"></canvas>
        </div>
        <div class="flex items-center gap-3 mt-3 text-xs text-q4cl-muted">
          <label class="flex items-center gap-1">{{ $t('線の太さ') }}
            <input type="range" min="1" max="6" step="0.5" v-model.number="penWidth" @change="applyPen" class="accent-q4cl-accent" :aria-label="$t('線の太さ')" />
            <span class="font-bold text-q4cl-primary w-6 text-right" aria-hidden="true">{{ penWidth }}</span>
          </label>
          <span class="ml-auto">{{ $t('指またはタッチペンで署名') }}</span>
        </div>
      </div>
      <footer class="px-5 py-3 border-t border-q4cl-border flex justify-end gap-2 flex-shrink-0">
        <button type="button" class="btn-secondary" @click="clear">{{ $t('クリア') }}</button>
        <button type="button" class="btn-secondary" @click="$emit('close')">{{ $t('キャンセル') }}</button>
        <button type="button" class="btn-primary" @click="save">{{ $t('確定') }}</button>
      </footer>
    </div>
  </div>
</template>

<script>
/**
 * signature_pad v5 を使用。Hi-DPI ハンドリングは公式 wiki に従う:
 * https://github.com/szimek/signature_pad#tips-and-tricks
 *
 * 重要ポイント:
 *  1. canvas の CSS 表示サイズ (offsetWidth/Height) と内部解像度 (canvas.width/height)
 *     の比率が ctx.scale(dpr, dpr) と一致していなければ座標がずれる。
 *  2. DOM が完全に layout されてから resize() を呼ぶ (RAF を 2 回または ResizeObserver)。
 *  3. canvas.width を変更すると context state がリセットされるので、scale も再適用必要。
 *  4. リサイズ時に既存ストロークを toData/fromData で復元 (clear だと描画消える)。
 */
export default {
  name: 'ModalSignpad',
  props: { initial: { type: String, default: '' } },
  emits: ['close', 'save'],
  setup(props, { emit }) {
    const canvasRef = Vue.ref(null);
    const wrapRef = Vue.ref(null);
    const penWidth = Vue.ref(2);
    let pad = null;
    let resizeObserver = null;
    let pendingResize = null;

    function applyPen() {
      if (pad) {
        pad.minWidth = penWidth.value * 0.5;
        pad.maxWidth = penWidth.value;
      }
    }

    function resize() {
      const canvas = canvasRef.value;
      if (!canvas) return;
      // getBoundingClientRect は小数 px を返す。CSS サイズと一致するので getBoundingClientRect を採用。
      const rect = canvas.getBoundingClientRect();
      const cssW = rect.width;
      const cssH = rect.height;
      if (cssW < 1 || cssH < 1) return; // レイアウト未完成

      const dpr = Math.max(window.devicePixelRatio || 1, 1);
      const internalW = Math.round(cssW * dpr);
      const internalH = Math.round(cssH * dpr);

      // 内部解像度が既に正しければ何もしない (signature_pad の状態を壊さないため)
      if (canvas.width === internalW && canvas.height === internalH) return;

      // 既存ストロークを退避
      let savedData = null;
      if (pad && typeof pad.toData === 'function') {
        try { savedData = pad.toData(); } catch (e) { savedData = null; }
      }

      // canvas.width を変えると context state は完全リセットされる
      canvas.width = internalW;
      canvas.height = internalH;
      // CSS pixel 座標で描画コマンドを受けて内部 px に展開
      canvas.getContext('2d').scale(dpr, dpr);

      // signature_pad に dpr を伝える (v5 は ratio オプションがあるが、コンストラクタ専用)
      // → 再描画は fromData で行う
      if (pad) {
        if (savedData && savedData.length) {
          try { pad.fromData(savedData); }
          catch (e) { pad.clear(); }
        } else {
          pad.clear();
        }
      }
    }

    function scheduleResize() {
      if (pendingResize) cancelAnimationFrame(pendingResize);
      pendingResize = requestAnimationFrame(() => {
        pendingResize = requestAnimationFrame(() => {
          resize();
        });
      });
    }

    function initPad() {
      // 旧版踏襲: 線色は純黒 #000000、 背景は白 (rgb(255,255,255))。
      // 透明背景にすると console 側の表示で署名が薄く見えるため、 旧版同様 白で固定。
      pad = new SignaturePad(canvasRef.value, {
        penColor: '#000000',
        backgroundColor: 'rgb(255,255,255)',
        minWidth: penWidth.value * 0.5,
        maxWidth: penWidth.value,
      });
    }

    Vue.onMounted(() => {
      // モーダルの transition / 描画完了を待つ
      Vue.nextTick(() => {
        // 2 RAF: 1 回目で layout, 2 回目で paint 確実化
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            initPad();
            resize();
            if (props.initial) {
              // 旧版踏襲: fromDataURL に ratio/width/height を渡す (旧 ModalSignpad.vue:127-131)
              try {
                const rect = canvasRef.value.getBoundingClientRect();
                const dpr = Math.max(window.devicePixelRatio || 1, 1);
                pad.fromDataURL(props.initial, { ratio: dpr, width: rect.width, height: rect.height });
              } catch (e) { console.warn('[ModalSignpad] fromDataURL failed:', e); }
            }
            // wrap サイズの変化に追従 (画面回転 / モーダル幅変化など)
            if (window.ResizeObserver && wrapRef.value) {
              resizeObserver = new ResizeObserver(() => scheduleResize());
              resizeObserver.observe(wrapRef.value);
            }
            window.addEventListener('resize', scheduleResize);
            window.addEventListener('orientationchange', scheduleResize);
          });
        });
      });
    });

    Vue.onBeforeUnmount(() => {
      window.removeEventListener('resize', scheduleResize);
      window.removeEventListener('orientationchange', scheduleResize);
      if (resizeObserver) {
        try { resizeObserver.disconnect(); } catch (e) { /* ignore */ }
        resizeObserver = null;
      }
      if (pad) {
        try { pad.off(); } catch (e) { /* ignore */ }
        pad = null;
      }
    });

    function clear() { if (pad) pad.clear(); }

    function save() {
      if (!pad) return;
      // 旧版踏襲: 空のまま保存しようとしたら警告 (旧 ModalSignpad.vue:176-179)
      if (pad.isEmpty()) {
        const msg = (window.i18nStore ? window.i18nStore.i18n('署名を入力してください') : '署名を入力してください');
        alert(msg);
        return;
      }
      const dataUrl = pad.toDataURL('image/webp');
      emit('save', dataUrl);
    }

    return { canvasRef, wrapRef, penWidth, clear, save, applyPen };
  }
};
</script>
